import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { companyPayouts, payoutReportUrl } from '../../api/company/client'
import { getCompanyToken } from 'shared/store/authStore'
import { format } from 'date-fns'
import { ArrowLeft, Loader2, Download } from 'lucide-react'

const STATUS_KEYS: Record<string, string> = {
  completed: 'company.statusCompleted',
  pending: 'company.statusPending',
  failed: 'company.statusFailed',
  scheduled: 'company.statusScheduled',
  processing: 'company.statusProcessing',
}

export default function CompanyPayoutDetail() {
  const { t } = useTranslation('portal')
  const { id } = useParams<{ id: string }>()
  const token = getCompanyToken()
  const { data, isLoading, error } = useQuery({
    queryKey: ['company', 'payout', id],
    queryFn: () => companyPayouts.get(id!).then((r) => r.data),
    enabled: !!id,
  })

  const downloadReport = (fmt: 'pdf' | 'csv') => {
    if (!id) return
    const url = payoutReportUrl(id, fmt)
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = u
        a.download = `payout-${id}.${fmt}`
        a.click()
        URL.revokeObjectURL(u)
      })
      .catch(() => window.open(url, '_blank'))
  }

  if (isLoading || !id) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" /> {t('company.payoutDetail.loading')}
      </div>
    )
  }

  if (error || !data) {
    return (
      <div>
        <p className="text-red-600">{t('company.payoutDetail.notFound')}</p>
        <Link to="/company/payouts" className="text-blue-600 hover:underline mt-2 inline-block">
          {t('company.payoutDetail.backToList')}
        </Link>
      </div>
    )
  }

  const transactions = (data.transactions ?? []) as Array<{
    payment_id?: string
    fiscal_number?: string
    is_internal_receipt?: boolean
    date?: string
    amount?: number
    bank_fee?: number
    platform_fee?: number
    net_amount?: number
  }>

  return (
    <div>
      <Link
        to="/company/payouts"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to payouts
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Payout: {format(new Date(data.period_start), 'MMM d')} –{' '}
          {format(new Date(data.period_end), 'MMM d, yyyy')}
        </h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => downloadReport('pdf')}
            className="rounded-lg bg-white border border-gray-300 hover:bg-gray-50 px-3 py-2 text-sm text-gray-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> PDF
          </button>
          <button
            type="button"
            onClick={() => downloadReport('csv')}
            className="rounded-lg bg-white border border-gray-300 hover:bg-gray-50 px-3 py-2 text-sm text-gray-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-gray-200 p-6 mb-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Summary</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-gray-500 text-sm">Total amount</p>
            <p className="text-gray-900">{Number(data.total_amount).toFixed(2)} {data.currency}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Bank fee</p>
            <p className="text-gray-700">{Number(data.total_bank_fee).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Platform fee</p>
            <p className="text-gray-700">{Number(data.total_platform_fee).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Net amount</p>
            <p className="text-blue-600 font-semibold">
              {Number(data.net_amount).toFixed(2)} {data.currency}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Status</p>
            <span
              className={`rounded px-2 py-0.5 text-xs ${
                data.status === 'completed'
                  ? 'bg-green-100 text-green-700'
                  : data.status === 'failed'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t(STATUS_KEYS[data.status] ?? data.status)}
            </span>
          </div>
          {data.processed_at && (
            <div>
              <p className="text-gray-500 text-sm">Processed</p>
              <p className="text-gray-700">
                {format(new Date(data.processed_at), 'PPpp')}
              </p>
            </div>
          )}
          {data.failure_reason && (
            <div className="sm:col-span-2">
              <p className="text-gray-500 text-sm">Failure reason</p>
              <p className="text-red-600 text-sm">{data.failure_reason}</p>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 p-4 border-b border-gray-200">
          Transactions ({data.transaction_count})
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[480px]">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500 text-sm">
                <th className="p-3">Fiscal #</th>
                <th className="p-3">Date</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Fees</th>
                <th className="p-3">Net</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, i) => (
                <tr key={tx.payment_id ?? i} className="border-b border-gray-100">
                  <td className="p-3">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-gray-700">{tx.fiscal_number ?? '—'}</span>
                      {tx.is_internal_receipt && (
                        <span className="rounded px-2 py-0.5 text-xs bg-violet-100 text-violet-700 whitespace-nowrap">
                          {t('company.transactions.internalReceipt')}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="p-3 text-gray-500 text-sm">{tx.date ?? '—'}</td>
                  <td className="p-3 text-gray-900">{Number(tx.amount ?? 0).toFixed(2)}</td>
                  <td className="p-3 text-gray-600">
                    {(Number(tx.bank_fee ?? 0) + Number(tx.platform_fee ?? 0)).toFixed(2)}
                  </td>
                  <td className="p-3 text-blue-600 font-medium">
                    {Number(tx.net_amount ?? 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
